// Load-time sanity checks for the balance tables. Types already guarantee completeness; these catch
// numeric typos that would otherwise surface as silent gameplay bugs.

import { ContentValidationError } from '../errors'
import { TRANSPORT_MODE_KEYS } from './keys'
import { BUILDINGS, CITIES, ECONOMY, GAME_MODES, STRIKES, TAX, TRANSPORT_MODES, TRANSPORT_ROUTING, UNITS, type CityGunDef, type ExtraEdgeLimit, type WarMapRules } from './tables'

function check(condition: boolean, message: string): void {
  if (!condition) throw new ContentValidationError(`balance: ${message}`)
}

const positive = (n: number): boolean => Number.isFinite(n) && n > 0
const nonNegative = (n: number): boolean => Number.isFinite(n) && n >= 0
const countRange = (min: number, max: number, least: number): boolean => Number.isInteger(min) && Number.isInteger(max) && min >= least && max >= min

/** A great-circle arc of this many degrees or more has no unique shortest path. */
const HALF_TURN_DEG = 180

const MAP_RULE_CHECKS: { readonly [K in WarMapRules['kind']]: (rules: Extract<WarMapRules, { readonly kind: K }>) => boolean } = {
  duel: (rules) => countRange(rules.neutralCapitalsMin, rules.neutralCapitalsMax, 0) && positive(rules.capitalDistanceMu),
  world: (rules) => positive(rules.muPerRadian),
}

const EXTRA_EDGE_LIMIT_CHECKS: { readonly [K in ExtraEdgeLimit['kind']]: (limit: Extract<ExtraEdgeLimit, { readonly kind: K }>) => boolean } = {
  mst_ratio: (limit) => positive(limit.ratio),
  arc_deg: (limit) => positive(limit.deg) && limit.deg < HALF_TURN_DEG,
}

/** A seat must always be able to field the largest unit. */
const LARGEST_UNIT_BODIES = Math.max(...Object.values(UNITS).map((unit) => unit.bodies))
const fraction = (n: number): boolean => Number.isFinite(n) && n >= 0 && n <= 1

function validateGameModes(): void {
  for (const [key, mode] of Object.entries(GAME_MODES)) {
    check(countRange(mode.belligerentsMin, mode.belligerentsMax, 2), `mode ${key} belligerents`)
    const cap = mode.bodyCap
    check(cap.kind === 'unlimited' || (Number.isInteger(cap.bodiesPerPlayer) && cap.bodiesPerPlayer >= LARGEST_UNIT_BODIES), `mode ${key} body cap`)
    check(fraction(mode.territoryWeight), `mode ${key} territory weight`)
    check(positive(mode.approvalEventScale) && mode.approvalEventScale <= 1, `mode ${key} approval event scale`)
    // A pull of 1 or more makes the free city nearest the enemy the cheapest everywhere: armies would swap continents.
    check(Number.isFinite(mode.expansionEnemyPull) && mode.expansionEnemyPull >= 0 && mode.expansionEnemyPull < 1, `mode ${key} expansion pull`)
    check(positive(mode.clock.regulationSeconds) && positive(mode.clock.overtimeSeconds), `mode ${key} clock`)
    const network = mode.network
    check(Number.isInteger(network.extraEdgeCandidatesPerCity) && network.extraEdgeCandidatesPerCity >= 0, `mode ${key} extra-edge candidates`)
    check(network.extraEdgeChance >= 0 && network.extraEdgeChance <= 1, `mode ${key} extra-edge chance`)
    const limitOk = EXTRA_EDGE_LIMIT_CHECKS[network.extraEdgeMax.kind] as (limit: ExtraEdgeLimit) => boolean
    check(limitOk(network.extraEdgeMax), `mode ${key} extra-edge limit`)
    const mapOk = MAP_RULE_CHECKS[mode.map.kind] as (rules: WarMapRules) => boolean
    check(mapOk(mode.map), `mode ${key} map rules`)
  }
}

export function validateBalance(): void {
  check(ECONOMY.startingTreasury <= ECONOMY.debtCeiling, 'starting treasury exceeds the debt ceiling')
  for (const [key, tax] of Object.entries(TAX)) check(positive(tax.revenueMultiplier), `tax ${key} multiplier`)
  for (const [key, u] of Object.entries(UNITS)) {
    check(positive(u.cost) && positive(u.bodies) && positive(u.hpPerBody) && positive(u.speed) && positive(u.sight), `unit ${key} core stats`)
    check(u.cost <= ECONOMY.debtCeiling, `unit ${key} costs more than the debt ceiling`)
    if (u.targetClass === 'none') {
      check(u.damage === 0 && u.range === 0, `unit ${key} has no target class but deals damage`)
    } else {
      check(positive(u.damage) && positive(u.attackPeriod) && positive(u.range) && nonNegative(u.minRange) && u.minRange < u.range, `unit ${key} weapon`)
    }
    if (u.spawner) check(positive(u.spawner.interval) && positive(u.spawner.maxAlive), `unit ${key} spawner`)
    // Only ground units can capture, and only they are cargo: the union already makes the others uncapturable.
    if (u.domain === 'land') {
      check(Number.isInteger(u.cargoSlotsPerBody) && positive(u.cargoSlotsPerBody), `unit ${key} cargo slots per body`)
      for (const mode of TRANSPORT_MODE_KEYS) {
        const slots = TRANSPORT_MODES[mode].cargoSlotsPerCraft
        check(u.bodies * u.cargoSlotsPerBody <= slots, `unit ${key} full group (${u.bodies} bodies x ${u.cargoSlotsPerBody}) does not fit one ${mode} transport (${slots})`)
      }
    } else {
      check(u.captures === false, `unit ${key} is ${u.domain} and captures; only land units may capture`)
    }
  }
  for (const mode of TRANSPORT_MODE_KEYS) {
    const t = TRANSPORT_MODES[mode]
    check(Number.isInteger(t.craftPerBerth) && positive(t.craftPerBerth), `${mode} transport craft per berth`)
    check(Number.isInteger(t.cargoSlotsPerCraft) && positive(t.cargoSlotsPerCraft), `${mode} transport cargo slots per craft`)
    check(positive(t.hp) && positive(t.speed) && positive(t.sight), `${mode} transport craft stats`)
    check(positive(t.replacementSeconds), `${mode} transport replacement interval`)
    check(positive(t.boardingCost) && positive(t.landingCost), `${mode} transport boarding/landing routing costs`)
  }
  check(positive(TRANSPORT_ROUTING.routingGroundSpeed), 'transport routing ground speed')
  check(Number.isInteger(TRANSPORT_ROUTING.groundPlannerGoalTreesPerSeat) && TRANSPORT_ROUTING.groundPlannerGoalTreesPerSeat >= 1, 'transport ground planner cache budget')
  // Below 1 an airlift slower than walking would still be taken; 1 would fly for any saving at all.
  check(Number.isFinite(TRANSPORT_ROUTING.airliftMinGroundRatio) && TRANSPORT_ROUTING.airliftMinGroundRatio >= 1, 'airlift minimum ground/air cost ratio')
  check(positive(TRANSPORT_ROUTING.waitFloorSeconds), 'transport wait floor')
  // A band ratio of 1 would never quantize; hysteresis must keep a band's floor reachable from below.
  check(Number.isFinite(TRANSPORT_ROUTING.waitLevelRatio) && TRANSPORT_ROUTING.waitLevelRatio > 1, 'transport wait band ratio')
  check(positive(TRANSPORT_ROUTING.waitRefreshSeconds), 'transport wait refresh period')
  check(TRANSPORT_ROUTING.waitLevelHysteresis > 0 && TRANSPORT_ROUTING.waitLevelHysteresis <= 1, 'transport wait hysteresis')
  check(Number.isFinite(TRANSPORT_ROUTING.justInTimeSlackSeconds) && TRANSPORT_ROUTING.justInTimeSlackSeconds >= 0, 'transport just-in-time slack')
  // A backlog under the wait floor would be one the routes do not even price.
  check(Number.isFinite(TRANSPORT_ROUTING.backlogWaitSeconds) && TRANSPORT_ROUTING.backlogWaitSeconds >= TRANSPORT_ROUTING.waitFloorSeconds, 'transport backlog wait')
  for (const [key, c] of Object.entries(CITIES)) {
    const gun: CityGunDef | null = c.gun
    check(positive(c.hp) && (gun === null || (positive(gun.damage) && positive(gun.period) && positive(gun.range))), `city ${key}`)
  }
  for (const [key, b] of Object.entries(BUILDINGS)) check(positive(b.cost) && b.cost <= ECONOMY.debtCeiling, `building ${key} cost`)
  for (const [key, s] of Object.entries(STRIKES)) {
    check(positive(s.cost) && s.cost <= ECONOMY.debtCeiling, `strike ${key} cost`)
    if (s.effect.kind === 'missile') check(positive(s.effect.damage) && positive(s.effect.salvos) && positive(s.effect.salvoIntervalSeconds) && positive(s.effect.speed), `strike ${key} missile`)
  }
  validateGameModes()
}
